import { Component, Inject, OnDestroy } from '@angular/core';
import { Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { MatSelect, MAT_SELECT_SCROLL_STRATEGY } from '@angular/material/select';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import {
  handleSegmentedArrowNav,
} from '../utils/routing-conversion-type.util';
import { RoutingMatSelectOverlayHelper } from '../utils/routing-mat-select-overlay.util';

import {
  applyHl7PersistedPathToModel,
  buildHl7PathModelForTargetSegment,
  parseHl7ComponentKeyParts,
  serializeHl7PathFromBuildPathModel,
} from '../hl7/hl7-path-step-builder';
import type {
  PathBuildAttributeOption,
  PathBuildElementStep,
  PathBuildModel,
} from '../xml/xml-path-step-builder';
import {
  applyPersistedPathToModel,
  getRepeatableStepIndices,
  mergeNodePredicateSpecsIntoSteps,
  serializePathModelToPath,
} from '../xml/xml-path-step-builder';
import { serializeHl7PathModelToPath } from '../hl7/hl7-path-step-builder';

export interface Hl7SegmentPickerOption {
  label: string;
  path: string;
  isRepeatable?: boolean;
}

export interface BuildFieldPathDialogData {
  fieldLabel?: string;
  model?: PathBuildModel;
  initialPath?: string;
  nodePredicateSpecs?: { node: string; attribute: string; value?: string }[];
  hl7SerializeTemplate?: string;
  /** HL7: same multi-step UI; Component/SubComponent per segment instead of attribute filters. */
  hl7Picker?: {
    segmentOptions: Hl7SegmentPickerOption[];
    indexOptions: number[];
    targetSegment: string;
    initialFieldIndex?: number | string | null;
    initialComponentIndex?: number | string | null;
    initialSubComponentIndex?: number | string | null;
    /** Full flat key from the rules form (e.g. OBR.4.4.4); preferred over separate index fields. */
    initialDisplayKey?: string;
    initialPath?: string;
  };
}

export interface BuildFieldPathDialogResult {
  path: string;
  displayKey?: string;
}

export function buildFieldPathMatSelectScrollStrategyFactory(
  overlay: Overlay
): () => ScrollStrategy {
  return () => overlay.scrollStrategies.reposition();
}

@Component({
  selector: 'app-build-field-path-dialog',
  templateUrl: './build-field-path-dialog.component.html',
  styleUrls: ['./build-field-path-dialog.component.scss'],
  providers: [
    {
      provide: MAT_SELECT_SCROLL_STRATEGY,
      deps: [Overlay],
      useFactory: buildFieldPathMatSelectScrollStrategyFactory,
    },
  ],
})
export class BuildFieldPathDialogComponent implements OnDestroy {
  private readonly matSelectOverlay = new RoutingMatSelectOverlayHelper();
  readonly form: FormGroup;
  readonly pathModel: PathBuildModel;
  readonly repeatableStepIndices: number[];
  readonly hl7Mode: boolean;
  readonly hl7IndexOptions: number[];
  applyBlockedMessage = '';

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialogRef: MatDialogRef<
      BuildFieldPathDialogComponent,
      BuildFieldPathDialogResult | undefined
    >,
    @Inject(MAT_DIALOG_DATA) readonly data: BuildFieldPathDialogData
  ) {
    this.hl7Mode = !!data.hl7Picker;
    this.hl7IndexOptions = data.hl7Picker?.indexOptions ?? [];

    if (this.hl7Mode) {
      const p = data.hl7Picker!;
      const target = (p.targetSegment ?? '').toString().trim().toUpperCase();
      let fi =
        p.initialFieldIndex != null && `${p.initialFieldIndex}`.trim() !== '' ? `${p.initialFieldIndex}` : '';
      const compRaw = p.initialComponentIndex;
      let ci =
        compRaw !== null && compRaw !== undefined && `${compRaw}`.trim() !== ''
          ? `${compRaw}`.trim()
          : '';
      const subRaw = p.initialSubComponentIndex;
      let sci =
        subRaw !== null && subRaw !== undefined && `${subRaw}`.trim() !== ''
          ? `${subRaw}`.trim()
          : '';
      const fromDisplayKey = (p.initialDisplayKey ?? '').trim();
      const keyMatch = /^([A-Za-z0-9]{2,4})\.(\d+)(?:\.(\d+))?(?:\.(\d+))?$/.exec(fromDisplayKey);
      if (keyMatch && keyMatch[1].toUpperCase() === target) {
        fi = keyMatch[2];
        ci = keyMatch[3] ?? '';
        sci = keyMatch[4] ?? '';
      }
      const displayKey =
        keyMatch && keyMatch[1].toUpperCase() === target
          ? fromDisplayKey
          : this.hl7FlatDisplayKey(target, fi, ci, sci);
      const segOpt =
        p.segmentOptions.find((o) => o.label.toUpperCase() === target) ??
        p.segmentOptions.find((o) => o.path.toUpperCase().endsWith(`/${target}`));
      const templatePath = (segOpt?.path ?? `HL7Message/${target}`).trim();
      const pathBuild = buildHl7PathModelForTargetSegment(target, displayKey, templatePath);
      if (!pathBuild) {
        this.pathModel = { steps: [], repeatableStepIndices: [], leafAttribute: displayKey, leafAttributeOptions: [] };
        this.repeatableStepIndices = [];
        this.form = this.fb.group({
          repeatableSteps: this.fb.array([]),
          hl7Leaf: this.buildHl7LeafGroup(fi, ci, sci),
        });
        return;
      }
      this.pathModel = JSON.parse(JSON.stringify(pathBuild.model)) as PathBuildModel;
      const init = (p.initialPath ?? data.initialPath ?? '').trim();
      if (init) {
        applyHl7PersistedPathToModel(this.pathModel, init, pathBuild.serializeTemplate);
        this.clearHl7RepeatableStepIndices();
      } else {
        this.resetHl7RepeatableStepsForNewPath();
      }
      const appliedLeaf = (this.pathModel.leafAttribute || '').trim();
      if (
        displayKey &&
        /^[A-Za-z0-9]{2,4}\.\d+/.test(displayKey) &&
        this.hl7FlatKeyIndexCount(displayKey) >= this.hl7FlatKeyIndexCount(appliedLeaf)
      ) {
        this.pathModel.leafAttribute = displayKey;
      } else if (!appliedLeaf || !/^[A-Za-z0-9]{2,4}\.\d+/.test(appliedLeaf)) {
        this.pathModel.leafAttribute = displayKey;
      }
      let leafFi = fi;
      let leafCi = ci;
      let leafSci = sci;
      const leafMatch = /^([A-Za-z0-9]{2,4})\.(\d+)(?:\.(\d+))?(?:\.(\d+))?$/.exec(
        (this.pathModel.leafAttribute || '').trim()
      );
      if (leafMatch && leafMatch[1].toUpperCase() === target) {
        leafFi = leafMatch[2];
        leafCi = leafMatch[3] ?? '';
        leafSci = leafMatch[4] ?? '';
      }
      this.repeatableStepIndices = this.hl7RepeatableIndicesForTarget(target);
      this.form = this.fb.group({
        repeatableSteps: this.fb.array(this.repeatableStepIndices.map((stepIdx) => this.buildHl7StepGroup(stepIdx))),
        hl7Leaf: this.buildHl7LeafGroup(leafFi, leafCi, leafSci),
      });
      if (init) {
        if (
          this.hl7FlatKeyIndexCount(displayKey) > this.hl7FlatKeyIndexCount((this.pathModel.leafAttribute || '').trim())
        ) {
          this.pathModel.leafAttribute = displayKey;
        }
        this.syncHl7LeafFormFromModel();
      }
      return;
    }

    this.pathModel = JSON.parse(JSON.stringify(data.model)) as PathBuildModel;
    mergeNodePredicateSpecsIntoSteps(this.pathModel, data.nodePredicateSpecs ?? []);
    const init = (data.initialPath || '').trim();
    const hl7Tpl = (data.hl7SerializeTemplate || '').trim();
    if (init) {
      if (hl7Tpl) {
        applyHl7PersistedPathToModel(this.pathModel, init, hl7Tpl);
      } else {
        applyPersistedPathToModel(this.pathModel, init);
      }
    }
    this.repeatableStepIndices = getRepeatableStepIndices(this.pathModel);
    this.form = this.fb.group({
      repeatableSteps: this.fb.array(
        this.repeatableStepIndices.map((stepIdx) => {
          const step = this.pathModel.steps[stepIdx];
          return this.fb.group({
            filters: this.fb.array(this.buildFiltersArray(step.filters)),
          });
        })
      ),
    });
  }

  ngOnDestroy(): void {
    this.matSelectOverlay.destroy();
  }

  onMatSelectOpenedChange(opened: boolean, select: MatSelect): void {
    this.matSelectOverlay.onOpenedChange(opened, select);
  }

  /** Repeatable steps from the map chain up to and including the user-selected target segment. */
  private hl7RepeatableIndicesForTarget(targetSegment: string): number[] {
    const target = (targetSegment || '').trim().toUpperCase();
    const indices = getRepeatableStepIndices(this.pathModel);
    if (!target) {
      return indices;
    }
    const targetStepIdx = this.pathModel.steps.findIndex(
      (s) => s.isRepeatable && s.elementName.toUpperCase() === target
    );
    if (targetStepIdx < 0) {
      return indices;
    }
    return indices.filter((idx) => idx <= targetStepIdx);
  }

  private buildHl7StepGroup(stepIdx: number): FormGroup {
    const step = this.pathModel.steps[stepIdx];
    return this.fb.group({
      filters: this.fb.array(this.buildHl7FiltersArray(step, stepIdx)),
    });
  }

  get hl7TargetSegmentLabel(): string {
    return this.hl7TargetSegment();
  }

  get hl7LeafGroup(): FormGroup {
    return this.form.get('hl7Leaf') as FormGroup;
  }

  private hl7TargetSegment(): string {
    return (this.data.hl7Picker?.targetSegment ?? '').toString().trim().toUpperCase();
  }

  private buildHl7LeafGroup(
    fieldIndex: string,
    componentIndex: string,
    subComponentIndex = ''
  ): FormGroup {
    return this.fb.group({
      fieldIndex: [fieldIndex ? Number(fieldIndex) : null],
      componentIndex: [componentIndex ? Number(componentIndex) : null],
      subComponentIndex: [subComponentIndex ? Number(subComponentIndex) : null],
    });
  }

  private hl7FlatDisplayKey(
    segment: string,
    fieldIndex: string,
    componentIndex: string,
    subComponentIndex: string
  ): string {
    if (!fieldIndex) {
      return segment;
    }
    const parts = [segment, fieldIndex];
    if (componentIndex) {
      parts.push(componentIndex);
    }
    if (subComponentIndex && componentIndex) {
      parts.push(subComponentIndex);
    }
    return parts.join('.');
  }

  /** Counts numeric index segments after the HL7 segment name (e.g. OBX.2.3.2 → 3). */
  private hl7FlatKeyIndexCount(key: string): number {
    const m = /^[A-Za-z0-9]{2,4}\.([\d.]+)$/.exec((key || '').trim());
    if (!m) {
      return 0;
    }
    return m[1].split('.').filter(Boolean).length;
  }

  private syncHl7LeafFormFromModel(): void {
    const target = this.hl7TargetSegment();
    const fromModel = (this.pathModel.leafAttribute || '').trim();
    const modelMatch = /^([A-Za-z0-9]{2,4})\.(\d+)(?:\.(\d+))?(?:\.(\d+))?$/.exec(fromModel);
    if (!modelMatch || modelMatch[1].toUpperCase() !== target) {
      return;
    }
    this.hl7LeafGroup?.patchValue(
      {
        fieldIndex: Number(modelMatch[2]),
        componentIndex: modelMatch[3] ? Number(modelMatch[3]) : null,
        subComponentIndex: modelMatch[4] ? Number(modelMatch[4]) : null,
      },
      { emitEvent: false }
    );
  }

  /** Parent repeatable rows (e.g. OBR when target is OBX); target leaf step uses leaf + filter rows. */
  private isHl7ConditionOnlyStep(stepIdx: number): boolean {
    return !!this.pathModel.steps[stepIdx]?.isRepeatable && !this.isHl7LeafStep(stepIdx);
  }

  private isHl7LeafStep(stepIdx: number): boolean {
    const target = this.hl7TargetSegment();
    const step = this.pathModel.steps[stepIdx];
    return !!step?.isRepeatable && step.elementName.toUpperCase() === target;
  }

  /** New path: empty filters on all repeatable steps (leaf comes from main form / Selected Attribute). */
  private resetHl7RepeatableStepsForNewPath(): void {
    for (const step of this.pathModel.steps) {
      if (!step.isRepeatable) {
        continue;
      }
      step.hl7FieldIndex = '';
      step.hl7ComponentIndex = '';
      step.filters = [];
    }
  }

  /** After loading a persisted path, never use step picker indices in filter rows. */
  private clearHl7RepeatableStepIndices(): void {
    for (const step of this.pathModel.steps) {
      if (!step.isRepeatable) {
        continue;
      }
      step.hl7FieldIndex = '';
      step.hl7ComponentIndex = '';
    }
  }

  private hl7LeafFromPicker(): {
    fieldIndex: string;
    componentIndex: string;
    subComponentIndex: string;
    displayKey: string;
  } {
    const target = this.hl7TargetSegment();
    const leafGrp = this.hl7LeafGroup;
    if (leafGrp) {
      const fiRaw = leafGrp.get('fieldIndex')?.value;
      const fi = fiRaw != null && `${fiRaw}`.trim() !== '' ? `${fiRaw}`.trim() : '';
      const compRaw = leafGrp.get('componentIndex')?.value;
      const ci =
        compRaw != null && compRaw !== '' && `${compRaw}`.trim() !== '' ? `${compRaw}`.trim() : '';
      const subRaw = leafGrp.get('subComponentIndex')?.value;
      const sci =
        subRaw != null && subRaw !== '' && `${subRaw}`.trim() !== '' ? `${subRaw}`.trim() : '';
      if (fi) {
        const displayKey = this.hl7FlatDisplayKey(target, fi, ci, sci);
        return { fieldIndex: fi, componentIndex: ci, subComponentIndex: sci, displayKey };
      }
    }
    const fromModel = (this.pathModel.leafAttribute || '').trim();
    const modelMatch = /^([A-Za-z0-9]{2,4})\.(\d+)(?:\.(\d+))?(?:\.(\d+))?$/.exec(fromModel);
    if (modelMatch && modelMatch[1].toUpperCase() === target) {
      return {
        fieldIndex: modelMatch[2],
        componentIndex: modelMatch[3] ?? '',
        subComponentIndex: modelMatch[4] ?? '',
        displayKey: fromModel,
      };
    }
    const p = this.data.hl7Picker!;
    const fi =
      p.initialFieldIndex != null && `${p.initialFieldIndex}`.trim() !== '' ? `${p.initialFieldIndex}`.trim() : '';
    const compRaw = p.initialComponentIndex;
    const ci =
      compRaw !== null && compRaw !== undefined && `${compRaw}`.trim() !== ''
        ? `${compRaw}`.trim()
        : '';
    const subRaw = p.initialSubComponentIndex;
    const sci =
      subRaw !== null && subRaw !== undefined && `${subRaw}`.trim() !== ''
        ? `${subRaw}`.trim()
        : '';
    const displayKey = this.hl7FlatDisplayKey(target, fi, ci, sci);
    return { fieldIndex: fi, componentIndex: ci, subComponentIndex: sci, displayKey };
  }

  private buildHl7FiltersArray(step: PathBuildElementStep, stepIdx: number): FormGroup[] {
    const seg = step.elementName.toUpperCase();
    if (this.isHl7ConditionOnlyStep(stepIdx)) {
      const valueFilters = (step.filters ?? []).filter((r) => `${r.value ?? ''}`.toString().trim() !== '');
      if (!valueFilters.length) {
        return [this.hl7FilterRow('', '', '', '')];
      }
      return valueFilters.map((f) => {
        const parts = parseHl7ComponentKeyParts(seg, (f.attribute ?? '').toString());
        return this.hl7FilterRow(
          parts.fieldIndex,
          parts.componentIndex,
          parts.subComponentIndex,
          `${f.value ?? ''}`.toString()
        );
      });
    }
    const valueFilters = (step.filters ?? []).filter((r) => `${r.value ?? ''}`.toString().trim() !== '');
    if (!valueFilters.length) {
      return [this.hl7FilterRow('', '', '', '')];
    }
    return valueFilters.map((f) => {
      const parts = parseHl7ComponentKeyParts(seg, (f.attribute ?? '').toString());
      return this.hl7FilterRow(
        parts.fieldIndex,
        parts.componentIndex,
        parts.subComponentIndex,
        `${f.value ?? ''}`.toString()
      );
    });
  }

  private hl7FilterRow(
    fieldIndex: number | string | null,
    componentIndex: number | string | null,
    subComponentIndex: number | string | null,
    value: string
  ): FormGroup {
    return this.fb.group({
      fieldIndex: [this.hl7IndexToFormValue(fieldIndex)],
      componentIndex: [this.hl7IndexToFormValue(componentIndex)],
      subComponentIndex: [this.hl7IndexToFormValue(subComponentIndex)],
      value: [value],
    });
  }

  get repeatableStepsArray(): FormArray {
    return this.form.get('repeatableSteps') as FormArray;
  }

  metaAtRepeatable(ri: number): PathBuildElementStep {
    return this.pathModel.steps[this.repeatableStepIndices[ri]];
  }

  /**
   * Filter attribute options for Build Path rows — omit the selected leaf attribute on its owning step
   * (e.g. hide `name` when the field is Result.Observation.Name).
   */
  filterAttributeOptionsForStep(ri: number): PathBuildAttributeOption[] {
    const stepIdx = this.repeatableStepIndices[ri];
    const opts = [...(this.metaAtRepeatable(ri).attributeOptions ?? [])];
    if (this.hl7Mode) {
      return opts;
    }
    const leaf = this.stripAt((this.pathModel.leafAttribute || '').toString().trim());
    if (!leaf || stepIdx !== this.pathModel.steps.length - 1) {
      return opts;
    }
    return opts.filter((o) => (o.name || '').trim().toLowerCase() !== leaf.toLowerCase());
  }

  /** Step heading: JSON `label` when present, else XML element `name`. */
  segmentLabelAt(ri: number): string {
    const step = this.metaAtRepeatable(ri);
    const display = (step?.displayLabel ?? '').toString().trim();
    if (display) {
      return display;
    }
    return (step?.elementName ?? '').toString().trim();
  }

  onHl7FilterIndexInput(
    event: Event,
    ri: number,
    fi: number,
    controlName: 'fieldIndex' | 'componentIndex' | 'subComponentIndex'
  ): void {
    const el = event.target as HTMLInputElement;
    const digits = (el.value ?? '').replace(/\D/g, '').slice(0, 2);
    el.value = digits;
    const row = this.filtersArray(ri).at(fi) as FormGroup;
    row.get(controlName)?.setValue(digits, { emitEvent: false });
  }

  /** ArrowLeft/Right moves between field and component index inputs in HL7 filter rows. */
  onHl7FilterIndexArrowNav(event: KeyboardEvent): void {
    handleSegmentedArrowNav(event, '.bp-hl7-field-shell');
  }

  private hl7IndexToFormValue(v: number | string | null | undefined): string {
    if (v == null || `${v}`.trim() === '') {
      return '';
    }
    return `${v}`.trim();
  }

  filtersArray(ri: number): FormArray {
    return this.repeatableStepsArray.at(ri).get('filters') as FormArray;
  }

  get leafAttributeDisplay(): string {
    if (this.hl7Mode) {
      return this.hl7TargetDisplayKey() || '—';
    }
    return this.stripAt((this.pathModel.leafAttribute || '').toString().trim()) || '—';
  }

  private hl7TargetDisplayKey(): string {
    return this.hl7LeafFromPicker().displayKey || this.hl7TargetSegment();
  }

  addFilterRow(ri: number): void {
    this.filtersArray(ri).push(this.emptyFilterRow());
  }

  removeFilterRow(ri: number, filterIndex: number): void {
    const fa = this.filtersArray(ri);
    if (fa.length <= 1) {
      if (this.hl7Mode) {
        fa.at(0).patchValue({ fieldIndex: '', componentIndex: '', subComponentIndex: '', value: '' });
      } else {
        fa.at(0).patchValue({ attribute: '', value: '' });
      }
      fa.at(0).markAsUntouched({ onlySelf: true });
      return;
    }
    fa.removeAt(filterIndex);
  }

  onCancel(): void {
    this.dialogRef.close(undefined);
  }

  onApply(): void {
    this.applyBlockedMessage = '';
    if (this.hl7Mode) {
      const outModel: PathBuildModel = JSON.parse(JSON.stringify(this.pathModel));
      const leaf = this.hl7LeafFromPicker();
      const target = this.hl7TargetSegment();
      for (let ri = 0; ri < this.repeatableStepIndices.length; ri++) {
        const stepIdx = this.repeatableStepIndices[ri];
        const fa = this.filtersArray(ri);
        const filterRows: { attribute: string; value: string }[] = [];
        const segName = outModel.steps[stepIdx].elementName.toUpperCase();
        for (const ctrl of fa.controls) {
          const row = ctrl as FormGroup;
          const fiRaw = row.get('fieldIndex')?.value;
          const fi = fiRaw != null && `${fiRaw}`.trim() !== '' ? `${fiRaw}`.trim() : '';
          const compRaw = row.get('componentIndex')?.value;
          const ci =
            compRaw != null && compRaw !== '' && `${compRaw}`.trim() !== '' ? `${compRaw}`.trim() : '';
          const subRaw = row.get('subComponentIndex')?.value;
          const sci =
            subRaw != null && subRaw !== '' && `${subRaw}`.trim() !== '' ? `${subRaw}`.trim() : '';
          const val = (row.get('value')?.value ?? '').toString().trim();
          if (val && fi) {
            const attrKey = this.buildHl7FilterAttributeKey(segName, fi, ci, sci);
            filterRows.push({ attribute: attrKey, value: val });
          }
        }
        if (this.isHl7ConditionOnlyStep(stepIdx)) {
          outModel.steps[stepIdx].hl7FieldIndex = '';
          outModel.steps[stepIdx].hl7ComponentIndex = '';
          outModel.steps[stepIdx].filters = filterRows;
        } else if (this.isHl7LeafStep(stepIdx)) {
          outModel.steps[stepIdx].hl7FieldIndex = leaf.fieldIndex;
          outModel.steps[stepIdx].hl7ComponentIndex = leaf.componentIndex;
          outModel.steps[stepIdx].filters = filterRows;
        } else {
          let primaryFi = '';
          let primaryCi = '';
          for (const ctrl of fa.controls) {
            const row = ctrl as FormGroup;
            const fiRaw = row.get('fieldIndex')?.value;
            const fi = fiRaw != null && `${fiRaw}`.trim() !== '' ? `${fiRaw}`.trim() : '';
            if (!primaryFi && fi) {
              primaryFi = fi;
              const compRaw = row.get('componentIndex')?.value;
              primaryCi =
                compRaw != null && compRaw !== '' && `${compRaw}`.trim() !== ''
                  ? `${compRaw}`.trim()
                  : '';
            }
          }
          outModel.steps[stepIdx].hl7FieldIndex = primaryFi;
          outModel.steps[stepIdx].hl7ComponentIndex = primaryCi;
          outModel.steps[stepIdx].filters = filterRows;
        }
      }
      outModel.leafAttribute = leaf.displayKey;
      const targetStepIdx = outModel.steps.findIndex(
        (s) => s.isRepeatable && s.elementName.toUpperCase() === target
      );
      if (targetStepIdx >= 0) {
        for (let i = targetStepIdx + 1; i < outModel.steps.length; i++) {
          const step = outModel.steps[i];
          if (!step.isRepeatable) {
            continue;
          }
          step.hl7FieldIndex = '';
          step.hl7ComponentIndex = '';
          step.filters = [];
        }
      }
      const built = serializeHl7PathFromBuildPathModel(outModel, target);
      const displayKey = (outModel.leafAttribute || leaf.displayKey || '').trim();
      if (!(built.path || '').trim()) {
        this.applyBlockedMessage =
          'Could not build a path. Choose Component (and SubComponent if needed) under Selected Attribute, then try again.';
        this.form.markAllAsTouched();
        this.hl7LeafGroup?.markAllAsTouched();
        return;
      }
      if (!/^([A-Za-z0-9]{2,4})\.\d+/.test(displayKey)) {
        this.applyBlockedMessage =
          'Choose Component under Selected Attribute (the field this rule reads, not the repeatable-segment filters).';
        this.form.markAllAsTouched();
        this.hl7LeafGroup?.markAllAsTouched();
        return;
      }
      this.dialogRef.close({
        path: built.path,
        displayKey,
      });
      return;
    }

    const stepsFa = this.repeatableStepsArray;
    let invalid = false;
    for (let ri = 0; ri < this.repeatableStepIndices.length; ri++) {
      const fg = stepsFa.at(ri) as FormGroup;
      const filters = fg.get('filters') as FormArray;
      for (const c of filters.controls) {
        const cg = c as FormGroup;
        const a = this.stripAt((cg.get('attribute')?.value ?? '').toString().trim());
        const v = (cg.get('value')?.value ?? '').toString().trim();
        if ((a && !v) || (!a && v)) {
          invalid = true;
          cg.markAllAsTouched();
        }
      }
    }
    if (invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const outModel: PathBuildModel = JSON.parse(JSON.stringify(this.pathModel));
    outModel.leafAttribute = this.stripAt((this.pathModel.leafAttribute || '').toString().trim());

    for (let ri = 0; ri < this.repeatableStepIndices.length; ri++) {
      const stepIdx = this.repeatableStepIndices[ri];
      const fg = stepsFa.at(ri) as FormGroup;
      const fa = fg.get('filters') as FormArray;
      outModel.steps[stepIdx].filters = fa.controls
        .map((ctrl) => {
          const cg = ctrl as FormGroup;
          return {
            attribute: this.stripAt((cg.get('attribute')?.value ?? '').toString().trim()),
            value: (cg.get('value')?.value ?? '').toString().trim(),
          };
        })
        .filter((r) => r.attribute && r.value);
    }

    const hl7Tpl = (this.data.hl7SerializeTemplate || '').trim();
    this.dialogRef.close({
      path: hl7Tpl
        ? serializeHl7PathModelToPath(outModel, hl7Tpl)
        : serializePathModelToPath(outModel),
    });
  }

  private buildFiltersArray(initial: PathBuildElementStep['filters']): FormGroup[] {
    const rows =
      initial && initial.length
        ? [...initial.map((r) => this.filterRow(r.attribute, r.value))]
        : [this.emptyFilterRow()];
    return rows;
  }

  private emptyFilterRow(): FormGroup {
    if (this.hl7Mode) {
      return this.hl7FilterRow(null, null, null, '');
    }
    return this.filterRow('', '');
  }

  private buildHl7FilterAttributeKey(
    segment: string,
    fieldIndex: string,
    componentIndex: string,
    subComponentIndex: string
  ): string {
    const parts = [segment, fieldIndex];
    if (componentIndex) {
      parts.push(componentIndex);
    }
    if (subComponentIndex && componentIndex) {
      parts.push(subComponentIndex);
    }
    return parts.join('.');
  }

  private filterRow(attribute: string, value: string): FormGroup {
    return this.fb.group({
      attribute: this.fb.control(attribute, []),
      value: this.fb.control(value, []),
    });
  }

  private stripAt(s: string): string {
    return (s || '').replace(/^@+/, '').trim();
  }
}
